// Coaches list with control elements

import { Link } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import { useOrgUsers, useTeamsDirectory } from '../utils/hooks';
import { UsersRoles } from '../utils/enums';
import InviteButton from './InviteButton';
import FilterInput from './FilterInput';
import Spinner from './Spinner';
import Avatar from './Avatar';
import './CoachesList.scss';

export default function CoachesList() {
  // The organization's coaches, read only while this tab is open
  const { users: coaches, isLoaded } = useOrgUsers(UsersRoles.COACH);
  // Their teams by name, off the directory — the same organization-scoped,
  // active-only set the team list used to scan, resolved by id instead
  const { teamsById } = useTeamsDirectory();

  const [searchQuery, setSearchQuery] = useState('');

  const sortedCoaches = useMemo(() => {
    return (coaches ?? []).toSorted((a, b) =>
      a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })
    );
  }, [coaches]);

  const teamNamesOf = useCallback(
    (coach) =>
      (coach.teams ?? [])
        .map((teamId) => teamsById.get(teamId)?.name)
        .filter(Boolean),
    [teamsById],
  );

  const searchValues = useMemo(
    () => sortedCoaches.map((coach) => [coach.name, ...teamNamesOf(coach)]),
    [sortedCoaches, teamNamesOf],
  );

  const displayedCoaches = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sortedCoaches;
    return sortedCoaches.filter((_, i) =>
      searchValues[i].some((val) => val && val.toLowerCase().includes(q)),
    );
  }, [sortedCoaches, searchValues, searchQuery]);

  return (
    <div className='coaches-list'>
      <div className='sticky-top'>
        <div className='controls'>
          <div className='filters'>
            <FilterInput
              className='search'
              placeholder='Search'
              value={searchQuery}
              onChange={setSearchQuery}
              small
            />
          </div>

          <InviteButton
            tokenCreatePayload={{ role: UsersRoles.COACH }}
            invited='coaches'
          >
            Invite coaches
          </InviteButton>
        </div>
        {!(searchQuery.trim() && displayedCoaches.length === 0) && (
          <ul className='tab-header'>
            <li className='col-1' />
            <li className='col-2'>Name</li>
            <li className='col-3'>E-mail</li>
            <li className='col-4'>Phone</li>
            <li className='col-34'>E-mail, Phone</li>
            <li className='col-234'>Name, E-mail, Phone</li>
            <li className='col-5'>Teams</li>
          </ul>
        )}
      </div>

      {!isLoaded && <Spinner show inline />}
      {searchQuery.trim() && displayedCoaches.length === 0 ? (
        <h5 className='name'>No results found for your search</h5>
      ) : (
        <div className='tab-body'>
        {displayedCoaches.map((coach) => {
          const { id, name, email, phone } = coach;

          const coachTeamNames = teamNamesOf(coach);

          return (
            <Link
              key={id}
              to={`/coaches/${id}`}
              className={
                'coach'
              }
            >
              <Avatar user={coach} size={64} className='col-1' />
              <p className='col-2 upper font-medium'>{name}</p>
              <p className='col-3'>{email}</p>
              <p className='col-4'>{phone}</p>
              <div className='col-34'>
                <p>{email}</p>
                <p>{phone}</p>
              </div>
              <div className='col-234'>
                <div className='img-name-wrap'>
                  <Avatar user={coach} size={32} />
                  <p className='upper font-medium'>{name}</p>
                </div>
                <p>{email}</p>
                <p>{phone}</p>
              </div>
              <div className='col-5'>
                {coachTeamNames.map((teamName, idx) => (
                  <p key={idx}>{teamName}</p>
                ))}
              </div>
            </Link>
          );
        })}
      </div>
      )}
    </div>
  );
}
